import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { ImageOff } from "lucide-react";
import { parseLsToStudioModel, type AssetMapping } from "@lunaverse-ide/shared";
import { EmbeddedPlayer, type PlayEvent } from "@lunaverse-ide/shared/embedded-player/react";
import { computeStageState, type AssetUriMap, type StageState } from "@lunaverse-ide/shared/stage";
import { useTr, type Entry } from "../../i18n/context.js";
import { stageEpisode, studioCardIndexForLine } from "./stage-episode.js";
import "./CocosStepPreview.css";

const PLAYER_URL = "/cocos-player/index.html";
const BOOT_FALLBACK_MS = 8_000;

const S = {
  title: { zh: "游戏画面预览", en: "In-game preview" },
  booting: { zh: "正在启动真实游戏画面…", en: "Starting the real game renderer…" },
  failed: { zh: "预览暂时无法加载", en: "Preview could not load" },
  retry: { zh: "重试", en: "Retry" },
  openPlaytest: { zh: "打开试玩", en: "Open Playtest" },
  openAssets: { zh: "打开素材", en: "Open Assets" },
  checking: { zh: "正在检查剧本…", en: "Checking script…" },
  invalid: { zh: "这一步尚未通过检查", en: "This step has not passed validation" },
  missing: { zh: "素材未绑定", en: "Assets not linked" },
  missingCount: { zh: "缺 {count} 个素材", en: "{count} assets missing" },
  missingHint: {
    zh: "画面结构已经保留。生成或绑定素材后，背景与人物会在原位自动替换。",
    en: "The composition is preserved. Generate or bind assets to replace these labeled stand-ins in place.",
  },
  advance: { zh: "点击画面，进入下一帧", en: "Click the stage for the next frame" },
  advanceAction: { zh: "游戏画面预览，进入下一帧", en: "In-game preview, advance to the next frame" },
  lastFrame: { zh: "已经是最后一帧", en: "This is the final frame" },
  position: { zh: "{current} / {total}", en: "{current} / {total}" },
} satisfies Record<string, Entry>;

export interface StudioValidationState {
  readonly status: "idle" | "checking" | "valid" | "invalid" | "unavailable";
  readonly diagnostics?: readonly string[];
}

export interface CocosStepPreviewProps {
  readonly source: string;
  readonly selectedLine: number | null;
  readonly assets?: AssetMapping;
  readonly assetUriMap?: AssetUriMap;
  readonly protagonist?: string | null;
  readonly validation?: StudioValidationState;
  readonly onOpenAssets?: () => void;
  readonly onOpenPlaytest?: () => void;
  readonly onAdvance?: () => void;
  readonly canAdvance?: boolean;
  readonly framePosition?: { readonly current: number; readonly total: number };
}

type PreviewStatus = "loading" | "ready" | "error";

function missingAssets(state: StageState): string[] {
  const missing: string[] = [];
  if (state.bg && !state.bg.hasImage) missing.push(state.bg.name);
  for (const character of state.characters) {
    if (!character.hasImage) missing.push(`${character.name} · ${character.look}`);
  }
  if (state.cardKind === "cg" && state.cg && !state.cg.hasImage) missing.push(state.cg.name);
  return [...new Set(missing.filter(Boolean))];
}

function appendRetry(url: string, retry: number): string {
  if (retry === 0) return url;
  return `${url}${url.includes("?") ? "&" : "?"}retry=${retry}`;
}

export function CocosStepPreview({
  source,
  selectedLine,
  assets,
  assetUriMap,
  protagonist,
  validation,
  onOpenAssets,
  onOpenPlaytest,
  onAdvance,
  canAdvance = false,
  framePosition,
}: CocosStepPreviewProps): ReactElement {
  const tr = useTr();
  const liveState = useMemo(() => {
    const model = parseLsToStudioModel(source);
    const cardIndex = studioCardIndexForLine(source, selectedLine ?? 1);
    return computeStageState(model.cards, cardIndex, assets, assetUriMap, protagonist);
  }, [assetUriMap, assets, protagonist, selectedLine, source]);
  const [state, setState] = useState(liveState);
  const lastValidState = useRef(liveState);
  const validationStatus = validation?.status ?? "valid";
  useEffect(() => {
    if (validationStatus === "invalid") {
      setState(lastValidState.current);
      return undefined;
    }
    const timer = setTimeout(() => {
      setState(liveState);
      if (validationStatus !== "checking") lastValidState.current = liveState;
    }, 120);
    return () => clearTimeout(timer);
  }, [liveState, validationStatus]);

  const episode = useMemo(() => stageEpisode(state, protagonist), [protagonist, state]);
  const missing = useMemo(() => missingAssets(state), [state]);
  const [status, setStatus] = useState<PreviewStatus>("loading");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (status !== "loading") return undefined;
    const timer = setTimeout(() => setStatus("ready"), BOOT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [retry, status]);

  const handlePlayerEvent = useCallback((event: PlayEvent) => {
    if (event.event === "ready" || event.event === "loaded") {
      setStatus("ready");
    } else if (event.event === "iframe-error" || event.event === "error") {
      setStatus("error");
    }
  }, []);

  const handleRetry = useCallback(() => {
    setStatus("loading");
    setRetry((value) => value + 1);
  }, []);

  const cgUrl = state.cardKind === "cg" ? state.cg?.imageUrl : undefined;
  const cgIsVideo = Boolean(cgUrl && /\.(?:mp4|webm|mov)(?:$|[?#])/i.test(cgUrl));
  const frameIsAdvanceable = Boolean(onAdvance && canAdvance && status !== "error" && validationStatus !== "invalid");

  return (
    <section
      className="ls-cocos"
      aria-label={tr(S.title)}
      data-preview-state={status}
      data-source-validation={validationStatus}
    >
      <div
        className="ls-cocos__frame"
      >
        <EmbeddedPlayer
          playerUrl={appendRetry(PLAYER_URL, retry)}
          episode={episode}
          inlineEpisodeInUrl={false}
          onEvent={handlePlayerEvent}
          className="ls-cocos__player"
          title={tr(S.title)}
        />
        {cgUrl ? (
          cgIsVideo ? (
            <video className="ls-cocos__cg" src={`${cgUrl}#t=0.1`} controls playsInline preload="metadata" aria-label={tr(S.title)} />
          ) : (
            <img className="ls-cocos__cg" src={cgUrl} alt="" />
          )
        ) : null}
        {frameIsAdvanceable && !cgIsVideo ? (
          <button
            type="button"
            className="ls-cocos__tap-target"
            aria-label={tr(S.advanceAction)}
            onClick={onAdvance}
          />
        ) : null}

        {status === "loading" ? (
          <div className="ls-cocos__status" role="status" aria-live="polite">
            <span className="ls-cocos__spinner" aria-hidden="true" />
            <span>{tr(S.booting)}</span>
          </div>
        ) : null}
        {status === "error" ? (
          <div className="ls-cocos__status ls-cocos__status--error" role="alert">
            <strong>{tr(S.failed)}</strong>
            <div className="ls-cocos__actions">
              <button type="button" className="ls-cocos__retry" onClick={handleRetry}>{tr(S.retry)}</button>
              {onOpenPlaytest ? (
                <button type="button" className="ls-cocos__retry" onClick={onOpenPlaytest}>{tr(S.openPlaytest)}</button>
              ) : null}
            </div>
          </div>
        ) : null}
        {validationStatus === "checking" ? (
          <div className="ls-cocos__validation" role="status" aria-live="polite">{tr(S.checking)}</div>
        ) : null}
        {validationStatus === "invalid" ? (
          <div className="ls-cocos__validation ls-cocos__validation--error" role="alert">
            <strong>{tr(S.invalid)}</strong>
            {(validation?.diagnostics ?? []).slice(0, 2).map((diagnostic) => <span key={diagnostic}>{diagnostic}</span>)}
            {onOpenPlaytest ? (
              <button type="button" className="ls-cocos__inline-action" onClick={onOpenPlaytest}>{tr(S.openPlaytest)}</button>
            ) : null}
          </div>
        ) : null}
      </div>
      {framePosition || missing.length > 0 ? (
        <div className="ls-cocos__meta">
          {framePosition ? <span className="ls-cocos__position">{tr(S.position, framePosition)}</span> : null}
          {missing.length > 0 ? (
          <details className="ls-cocos__missing">
            <summary aria-label={tr(S.missingCount, { count: missing.length })}>
              <ImageOff size={14} aria-hidden="true" />
              <span>{tr(S.missingCount, { count: missing.length })}</span>
            </summary>
            <div className="ls-cocos__missing-panel">
              <strong>{tr(S.missing)}</strong>
              <span>{missing.slice(0, 3).join("、")}</span>
              <small>{tr(S.missingHint)}</small>
              {onOpenAssets ? (
                <button type="button" className="ls-cocos__inline-action" onClick={onOpenAssets}>{tr(S.openAssets)}</button>
              ) : null}
            </div>
          </details>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
